import { toast } from "sonner";
import { X, Download, UserCircle2, Calendar, Folder } from "lucide-react";
import { CR_STATUS_TONE, CR_SEVERITY_TONE, fmtDateTime, fmtDate } from "./clientUtils";
import CRCommentsThread from "@/components/shared/CRCommentsThread";

/**
 * Read-only modal that shows one change request in full:
 * title, meta chips (severity/status/raised-by/raised-at), full HTML description
 * (including any inline pasted images), expected outcome and attachments.
 */
export default function ClientCRDetailModal({ cr, token, clientId = "", onClose }) {
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  const downloadDoc = async (doc) => {
    try {
      const url = `${apiBase}/client/change-requests/${cr.id}/documents/${doc.id}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = doc.filename;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] flex flex-col rounded-lg border border-black/10 shadow-2xl overflow-hidden"
        data-testid={`client-cr-detail-${cr.id}`}>
        {/* Header */}
        <header className="flex items-start justify-between gap-4 p-5 border-b border-black/10 bg-[#f7f6f3]">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">Change request</div>
            <h3 className="mt-1 text-xl md:text-2xl font-bold text-[#0a0a0a] break-words">{cr.title}</h3>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className={`px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] border rounded-full ${CR_SEVERITY_TONE[cr.severity] || "bg-zinc-100 border-zinc-300"}`}>{cr.severity}</span>
              <span className={`px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] border rounded-full ${CR_STATUS_TONE[cr.status] || "bg-zinc-100 border-zinc-300"}`}>{cr.status}</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="client-cr-detail-close"
            className="p-1.5 rounded-full hover:bg-black/5 text-[#4a4a4a] hover:text-[#0a0a0a]"><X className="h-4 w-4" /></button>
        </header>

        {/* Meta */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-5 py-4 border-b border-black/10 text-xs">
          <div className="inline-flex items-start gap-2 text-[#4a4a4a]">
            <Folder className="h-3.5 w-3.5 mt-0.5 text-[#9a9a9a]" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#9a9a9a]">Project</div>
              <div className="text-[#0a0a0a] font-semibold">{cr.project_name || "—"}</div>
            </div>
          </div>
          <div className="inline-flex items-start gap-2 text-[#4a4a4a]">
            <UserCircle2 className="h-3.5 w-3.5 mt-0.5 text-[#9a9a9a]" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#9a9a9a]">Raised by</div>
              <div className="text-[#0a0a0a] font-semibold">{cr.raised_by_name || (cr.raised_by === "client" ? "Client" : "Reachvel")}</div>
            </div>
          </div>
          <div className="inline-flex items-start gap-2 text-[#4a4a4a]">
            <Calendar className="h-3.5 w-3.5 mt-0.5 text-[#9a9a9a]" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#9a9a9a]">Raised on</div>
              <div className="text-[#0a0a0a] font-semibold">{fmtDateTime(cr.raised_at || cr.created_at || cr.raised_date)}</div>
              {cr.closed_date && <div className="text-[10px] text-[#9a9a9a] mt-0.5">Closed {fmtDate(cr.closed_date)}</div>}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-5 py-5 space-y-4">
          <section>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a] mb-2">Description</div>
            {cr.description ? (
              <div className="text-sm text-[#0a0a0a] cr-body prose prose-sm max-w-none"
                style={{ lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{ __html: cr.description }} />
            ) : (
              <div className="text-sm text-[#9a9a9a] italic">No description provided.</div>
            )}
          </section>

          {cr.expectation && (
            <section className="p-3 rounded bg-amber-50 border border-amber-200">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-800">Expected outcome</div>
              <div className="mt-1 text-sm text-[#0a0a0a] whitespace-pre-wrap">{cr.expectation}</div>
            </section>
          )}

          {cr.documents_count > 0 && (
            <section>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a] mb-1">Attachments ({cr.documents_count})</div>
              <div className="flex flex-wrap gap-2">
                {cr.documents.map((d) => (
                  <button key={d.id} onClick={() => downloadDoc(d)} data-testid={`client-cr-doc-${d.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722]">
                    <Download className="h-3 w-3" /> {d.filename}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Two-way comments thread with Reachvel */}
          <CRCommentsThread crId={cr.id} token={token} mode="client" currentUserId={clientId} />
        </div>

        <style>{`
          .cr-body img { max-width: 100%; height: auto; border-radius: 6px; margin: 8px 0; display: block; }
          .cr-body a { color: #ff5722; text-decoration: underline; }
          .cr-body ul, .cr-body ol { padding-left: 22px; margin: 6px 0; }
          .cr-body p { margin: 6px 0; }
        `}</style>
      </div>
    </div>
  );
}
